import { create } from "zustand";
import { Capacitor } from "@capacitor/core";

/** Everything the visitor can switch on. Necessary storage is not a choice and not listed. */
export type ConsentCategory = "statistics" | "chat";
export const CONSENT_CATEGORIES: ConsentCategory[] = ["statistics", "chat"];

export type ConsentChoice = Record<ConsentCategory, boolean>;

export const NO_CONSENT: ConsentChoice = { statistics: false, chat: false };
export const ALL_CONSENT: ConsentChoice = { statistics: true, chat: true };

// Bump the version when a category's purpose changes; a new or removed category
// already invalidates old decisions through the stored category list.
const STORAGE_KEY = "managd-cookie-consent";
const CONSENT_VERSION = 1;
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

interface StoredConsent {
    version: number;
    categories: string[];
    choice: ConsentChoice;
    decidedAt: string;
}

/**
 * The native apps ask through the stores' own privacy flow, so the banner and
 * every gate behind it only apply in the browser.
 */
export const consentApplies = (): boolean => !Capacitor.isNativePlatform();

const readStored = (): ConsentChoice | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const stored = JSON.parse(raw) as StoredConsent;
        const sameCategories =
            Array.isArray(stored.categories) &&
            stored.categories.length === CONSENT_CATEGORIES.length &&
            CONSENT_CATEGORIES.every((category) => stored.categories.includes(category));
        const age = Date.now() - new Date(stored.decidedAt).getTime();
        if (stored.version !== CONSENT_VERSION || !sameCategories || !(age < MAX_AGE_MS)) return null;
        return CONSENT_CATEGORIES.reduce(
            (choice, category) => ({ ...choice, [category]: stored.choice?.[category] === true }),
            NO_CONSENT
        );
    } catch {
        return null;
    }
};

interface CookieConsentState {
    /** null until the visitor has decided, or when the decision expired. */
    choice: ConsentChoice | null;
    settingsOpen: boolean;
    save: (choice: ConsentChoice) => void;
    openSettings: () => void;
    closeSettings: () => void;
}

const useCookieConsentStore = create<CookieConsentState>((set) => ({
    // Read synchronously, so the first render already knows whether to ask.
    choice: consentApplies() ? readStored() : ALL_CONSENT,
    settingsOpen: false,
    save: (choice) => {
        const stored: StoredConsent = {
            version: CONSENT_VERSION,
            categories: CONSENT_CATEGORIES,
            choice,
            decidedAt: new Date().toISOString(),
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        } catch {
            // Private mode without storage: the choice holds for this visit.
        }
        set({ choice, settingsOpen: false });
    },
    openSettings: () => set({ settingsOpen: true }),
    closeSettings: () => set({ settingsOpen: false }),
}));

/** For code outside React, like the Sentry sampler. */
export const hasConsent = (category: ConsentCategory): boolean =>
    useCookieConsentStore.getState().choice?.[category] === true;

export default useCookieConsentStore;
